'use client';

import React from 'react';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GalleryExport } from 'iconsax-react';
import { useTranslation } from 'react-i18next';

import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';

import { useImageUploader, UploadedImage } from './useImageUploader';
import { SortableImageRow } from './SortableImageRow';

interface ImageUploaderProps {
  maxFiles?: number;
  maxSizeMB?: number;
  onImagesChange?: (images: UploadedImage[]) => void;
  onThumbnailChange?: (thumbnailId: string | null) => void;
  initialImages?: UploadedImage[];
}

export default function ImageUploader({
  maxFiles = 10,
  maxSizeMB = 5,
  onImagesChange,
  onThumbnailChange,
  initialImages = [],
}: ImageUploaderProps) {
  const { t } = useTranslation();

  const {
    images,
    thumbnailId,
    rejections,
    maxFileSize,
    setRejections,
    onDrop,
    handleDragEnd,
    handleSetThumbnail,
    handleRemove,
  } = useImageUploader({ maxFiles, maxSizeMB, initialImages, onImagesChange, onThumbnailChange });

  const isAtLimit = images.length >= maxFiles;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (fileRejections) => setRejections(fileRejections),
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    maxFiles: maxFiles - images.length,
    maxSize: maxFileSize,
    disabled: isAtLimit,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const hasTooManyFilesError = rejections.some((r) =>
    r.errors.some((e) => e.code === 'too-many-files')
  );

  const hasFileTooLargeError = rejections.some((r) =>
    r.errors.some((e) => e.code === 'file-too-large')
  );

  const hasInvalidTypeError = rejections.some((r) =>
    r.errors.some((e) => e.code === 'file-invalid-type')
  );

  const otherRejections = rejections.filter((r) =>
    !r.errors.some((e) => ['too-many-files', 'file-too-large', 'file-invalid-type'].includes(e.code))
  );

  return (
    <div data-testid="image-uploader-root" className="w-full font-sans">
      {/* Header */}
      <div data-testid="image-uploader-header" className="flex justify-between items-center mb-4">
        <TitleText
          text={t('imagesTitle')}
          size="lg"
          className="!font-semibold text-gray-800 dark:text-neutral-100 text-[16px] sm:text-[18px]"
        />
        <InfoText
          text={`${images.length}/${maxFiles} ${t('imagesTitle').toLowerCase()}`}
          size="sm"
          className="!text-[#777777] dark:!text-[#777777] !text-[14px] !font-normal !leading-none"
        />
      </div>

      {/* Drop zone */}
      <div
        {...getRootProps()}
        data-testid="image-uploader-dropzone"
        className={`border border-dashed rounded-xl py-4 sm:py-5 px-4 sm:px-6
          text-center cursor-pointer transition-all duration-300 ease-in-out ${(rejections.length > 0 || images.length > 0) ? 'mb-4' : ''}
          ${isAtLimit
            ? 'border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800/50 cursor-not-allowed'
            : isDragActive
              ? 'border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
              : 'border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:bg-gray-50/50 dark:hover:bg-neutral-800/50'
          }`}
      >
        <input {...getInputProps()} />
        <div data-testid="image-uploader-dropzone-content" className="flex flex-col items-center justify-center">
          <div
            data-testid="image-uploader-dropzone-icon"
            className="relative flex items-center justify-center w-[48px] h-[48px] sm:w-[52px] sm:h-[52px]
              bg-gray-50 dark:bg-neutral-800 rounded-xl mb-4 border border-gray-100 dark:border-neutral-700"
          >
            <GalleryExport size="26" className="text-gray-600 dark:text-neutral-400" variant="Outline" />
          </div>
          <TitleText
            text={t('dropzoneHeading')}
            size="base"
            className="!font-medium text-gray-800 dark:text-neutral-100 mb-1 text-[16px]"
          />
          <InfoText
            text={t('dropzoneAcceptedTypes', { types: '[.jpg, .png, .jpeg, .webp]' })}
            size="xs"
            className="text-gray-400 dark:text-neutral-500"
          />
          <InfoText
            text={t('dropzoneLimit', { maxFiles, maxSize: maxSizeMB })}
            size="xs"
            className="text-gray-400 dark:text-neutral-500 mt-1"
          />
        </div>
      </div>

      {/* Rejection errors */}
      {rejections.length > 0 && (
        <div data-testid="image-uploader-rejections" className="mb-6 flex flex-col gap-2">
          {hasTooManyFilesError && (
            <div
              data-testid="image-uploader-rejection-alert-too-many"
              className="flex items-start gap-3 text-sm bg-red-50 dark:bg-red-900/10
                border border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-3"
            >
              <InfoText text="⚠️" className="mt-0.5 flex-shrink-0" />
              <div data-testid="image-uploader-rejection-details-too-many">
                <InfoText
                  className="block text-red-500 font-medium"
                  data-testid="image-uploader-rejection-error-too-many-files"
                  text={t('tooManyFilesError', { maxFiles })}
                />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setRejections((r) => r.filter((item) => !item.errors.some(err => err.code === 'too-many-files')));
                }}
                data-testid="image-uploader-rejection-close-too-many"
                className="ml-auto text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 font-medium"
              >
                ✕
              </button>
            </div>
          )}
          {hasFileTooLargeError && (
            <div
              data-testid="image-uploader-rejection-alert-too-large"
              className="flex items-start gap-3 text-sm bg-red-50 dark:bg-red-900/10
                border border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-3"
            >
              <InfoText text="⚠️" className="mt-0.5 flex-shrink-0" />
              <div data-testid="image-uploader-rejection-details-too-large">
                <InfoText
                  className="block text-red-500 font-medium"
                  data-testid="image-uploader-rejection-error-too-large"
                  text={t('fileTooLargeError', { maxSize: maxSizeMB })}
                />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setRejections((r) => r.filter((item) => !item.errors.some(err => err.code === 'file-too-large')));
                }}
                data-testid="image-uploader-rejection-close-too-large"
                className="ml-auto text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 font-medium"
              >
                ✕
              </button>
            </div>
          )}
          {hasInvalidTypeError && (
            <div
              data-testid="image-uploader-rejection-alert-invalid-type"
              className="flex items-start gap-3 text-sm bg-red-50 dark:bg-red-900/10
                border border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-3"
            >
              <InfoText text="⚠️" className="mt-0.5 flex-shrink-0" />
              <div data-testid="image-uploader-rejection-details-invalid-type">
                <InfoText
                  className="block text-red-500 font-medium"
                  data-testid="image-uploader-rejection-error-invalid-type"
                  text={t('unsupportedFileTypeError')}
                />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setRejections((r) => r.filter((item) => !item.errors.some(err => err.code === 'file-invalid-type')));
                }}
                data-testid="image-uploader-rejection-close-invalid-type"
                className="ml-auto text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 font-medium"
              >
                ✕
              </button>
            </div>
          )}
          {otherRejections.map(({ file, errors }, index) => (
            <div
              key={`${file.name}-${index}`}
              data-testid={`image-uploader-rejection-alert-${index}`}
              className="flex items-start gap-3 text-sm bg-red-50 dark:bg-red-900/10
                border border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-3"
            >
              <InfoText text="⚠️" className="mt-0.5 flex-shrink-0" />
              <div data-testid={`image-uploader-rejection-details-${index}`}>
                <TitleText text={file.name} size="sm" className="font-semibold" />
                {errors.map((e) => (
                  <InfoText
                    key={e.code}
                    className="block text-red-500 mt-0.5"
                    data-testid={`image-uploader-rejection-error-${e.code}`}
                    text={e.message}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setRejections((r) => r.filter((rf) => rf.file !== file));
                }}
                data-testid={`image-uploader-rejection-close-${index}`}
                className="ml-auto text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 font-medium"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Sortable image list */}
      {images.length > 0 && (
        <div
          data-testid="image-uploader-list-container"
          className="border border-gray-100 dark:border-neutral-800 rounded-xl overflow-hidden
            bg-white dark:bg-neutral-900"
        >
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={images.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div data-testid="image-uploader-list" className="flex flex-col">
                {images.map((image) => (
                  <SortableImageRow
                    key={image.id}
                    image={image}
                    isThumbnail={thumbnailId === image.id}
                    onSetThumbnail={handleSetThumbnail}
                    onRemove={handleRemove}
                    disableReorder={images.length <= 1}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
